import { HttpClient, HttpErrorResponse, HttpHeaders } from '@angular/common/http';
import { Injectable, type Signal, computed, inject, signal } from '@angular/core';
import { firstValueFrom } from 'rxjs';

import type { TargetSurface } from '@vevents/auth';
import {
  PreferencesService,
  PushNotificationsService,
  isNativePlatform,
  isPluginAvailable,
  type PushPermission
} from '@vevents/native-services';

import { environment } from '../../../environments/environment';
import { AuthService } from '../services/auth.service';
import { DeepLinkHandlerService } from '../services/deep-link-handler.service';
import { DeviceInstallIdService } from '../services/device-install-id.service';
import { SdkRuntimeConfigService } from '../sdk/sdk-runtime-config.service';
import type {
  MobilePushPayloadData,
  MobilePushTap,
  PushRoutingDecision
} from './push-payload.model';

/**
 * MOB-100 Stage 26 — push permission, registration and tap routing for
 * `apps/mobile`.
 *
 * The Stage 17 `PushNotificationsService` wrapper handles raw plugin
 * lookup + permission probe. Stage 26 layers the BRD-mandated mobile UX
 * on top:
 *
 *  - `requestPermissionAndRegister()` is called by
 *    `PushPermissionPromptComponent` AFTER the Hub has rendered
 *    (`markHubRendered()`). Prompting before Hub renders has dramatically
 *    lower acceptance rates per BRD §4.11.1, so this service refuses to
 *    open the system dialog until `hubRendered()` flips true.
 *  - On the native `registration` event we POST the issued APNs/FCM
 *    token to `/api/mobile/device/push-token` (Stage 9 endpoint). The
 *    request is single-flight and idempotent on the BFF side.
 *  - On `pushNotificationActionPerformed` we extract the target from the
 *    payload and route via `DeepLinkHandlerService` — the same handler
 *    that owns Universal-Link / App-Link taps.
 *
 * The service is intentionally framework-light: it holds three signals
 * (`permission`, `hubRendered`, `shouldPrompt`) and a couple of
 * imperative methods. Components subscribe to the signals; the
 * `PushPermissionPromptComponent` opens / closes itself off
 * `shouldPrompt()`.
 */
const PROMPT_SHOWN_KEY = 'mob.push.firstPromptShown.v1';
const PERMISSION_CACHED_KEY = 'mob.push.permission.v1';

@Injectable({ providedIn: 'root' })
export class MobilePushService {
  private readonly http = inject(HttpClient);
  private readonly push = inject(PushNotificationsService);
  private readonly prefs = inject(PreferencesService);
  private readonly auth = inject(AuthService);
  private readonly device = inject(DeviceInstallIdService);
  private readonly deepLink = inject(DeepLinkHandlerService);
  private readonly sdkConfig = inject(SdkRuntimeConfigService);

  private readonly _permission = signal<PushPermission>('prompt');
  private readonly _hubRendered = signal(false);
  /** Last token registered with the BFF. Memory only — not persisted. */
  private readonly _registeredToken = signal<string | null>(null);
  private listenerInstalled = false;
  private requestInFlight: Promise<{ granted: boolean; token: string | null }> | null = null;

  readonly permission: Signal<PushPermission> = this._permission.asReadonly();
  readonly hubRendered = this._hubRendered.asReadonly();
  readonly registeredToken = this._registeredToken.asReadonly();

  /**
   * True when we should open the `PushPermissionPromptComponent`. Four
   * gates:
   *   1. User is authenticated.
   *   2. Hub has rendered at least once this launch.
   *   3. The first-launch prompt has not been shown yet.
   *   4. System permission state is still `prompt` (neither granted nor
   *      denied — once denied, the BRD says we hand the user off to
   *      Settings > Notifications, not re-prompt).
   */
  readonly shouldPrompt = computed(() =>
    // F-SDK REQ-F-SDK-040 — inside a partner application the host owns the
    // notification permission prompt and the APNs/FCM registration. Prompting
    // from the embedded web layer would either be ignored (iOS, where the
    // host owns the application delegate) or would produce a second,
    // confusing permission dialogue attributed to the host's brand.
    !this.sdkConfig.isEmbedded() &&
    this.auth.isAuthenticated() &&
    this._hubRendered() &&
    !this._firstPromptShown() &&
    this._permission() === 'prompt'
  );

  // Must be a signal: `shouldPrompt` is a computed, and a plain class
  // field would never invalidate it — "Not now" would set the flag but
  // the prompt dialog would stay on screen forever.
  private readonly _firstPromptShown = signal(false);
  private hydratedFromPrefs = false;

  /** Hydrate prompt-shown flag + cached permission from preferences. */
  async hydrate(): Promise<void> {
    if (this.hydratedFromPrefs) return;
    this.hydratedFromPrefs = true;
    const [shown, cached] = await Promise.all([
      this.prefs.get(PROMPT_SHOWN_KEY),
      this.prefs.get(PERMISSION_CACHED_KEY)
    ]);
    this._firstPromptShown.set(shown === '1');
    if (cached === 'granted' || cached === 'denied' || cached === 'prompt') {
      this._permission.set(cached);
    }
  }

  /**
   * Called by the Stage 19 Hub (and the Stage 17 home placeholder until
   * then) as soon as its first paint commits. Idempotent.
   */
  markHubRendered(): void {
    if (this._hubRendered()) return;
    // Hydrate the persisted prompt-shown flag BEFORE opening the prompt
    // gate — without this, every app launch re-prompted users who had
    // already dismissed the dialog (the stored flag was never read).
    void this.hydrate().finally(() => {
      this._hubRendered.set(true);
      void this.syncRegistrationIfGranted();
    });
  }

  /**
   * Launch-time token sync. The first-launch prompt only fires ONCE per
   * install — if the user granted it back then, no later launch ever
   * called `PushNotifications.register()` again, so an FCM/APNs token
   * rotation, an app update, or a wiped `mobile.PushToken` table meant
   * the BFF had no live token and background pushes reported
   * "0 recipients". Re-registering on every launch is idempotent: the
   * BFF upserts on (DeviceInstallId, TenantId) and refreshes the row's
   * EventId/ChatUserId so broadcasts for the CURRENT event reach this
   * device.
   */
  async syncRegistrationIfGranted(): Promise<void> {
    // F-SDK REQ-F-SDK-040 — the SDK MUST NOT initiate its own registration.
    // Host-supplied tokens arrive through registerHostPushToken instead.
    if (this.sdkConfig.isEmbedded()) return;
    if (!isNativePlatform() || !isPluginAvailable('PushNotifications')) return;
    // checkPermissions() reads state without opening the system dialog.
    const current = await this.push.checkPermission();
    this._permission.set(current);
    await this.prefs.set(PERMISSION_CACHED_KEY, current);
    if (current !== 'granted') return;

    await this.installListeners();
    try {
      await this.push.register();
    } catch (err) {
      console.warn('[MobilePushService] launch-time register() failed:', err);
    }
  }

  /**
   * Mark the first-launch prompt as shown so we never re-open it (BRD:
   * "do not re-prompt after dismissal — link to system settings instead").
   * Called whether the user accepted or dismissed.
   */
  async markPromptShown(): Promise<void> {
    this._firstPromptShown.set(true);
    await this.prefs.set(PROMPT_SHOWN_KEY, '1');
  }

  /**
   * Open the system permission dialog and, on grant, register for push
   * + POST the token to `/api/mobile/device/push-token`.
   *
   * The result is cached in preferences so a subsequent app launch
   * knows whether the system already granted/denied without re-asking.
   */
  async requestPermissionAndRegister(): Promise<{ granted: boolean; token: string | null }> {
    if (this.sdkConfig.isEmbedded()) {
      return { granted: false, token: null };
    }
    if (this.requestInFlight) return this.requestInFlight;
    this.requestInFlight = this.doRequestPermissionAndRegister().finally(() => {
      this.requestInFlight = null;
    });
    return this.requestInFlight;
  }

  private async doRequestPermissionAndRegister(): Promise<{ granted: boolean; token: string | null }> {
    const result: PushPermission = await this.push.requestPermission();
    this._permission.set(result);
    await this.prefs.set(PERMISSION_CACHED_KEY, result);
    await this.markPromptShown();

    if (result !== 'granted') {
      return { granted: false, token: null };
    }

    this.installListeners();
    try {
      await this.push.register();
    } catch (err) {
      console.warn('[MobilePushService] PushNotifications.register() failed:', err);
      return { granted: true, token: null };
    }
    // Token arrives asynchronously on the `registration` event — the
    // listener pipes it through `onRegistration()` which calls
    // `registerTokenWithBff()`. Return what we know synchronously.
    return { granted: true, token: this._registeredToken() };
  }

  /**
   * BRD §4.11.1 edge case: "Push permission denied — Settings > Notifications
   * shows 'Open system settings' to re-enable." This helper opens the
   * system settings via the `App.openSettings()` API when supported,
   * falling back gracefully on platforms that don't expose it (web).
   */
  async openSystemSettings(): Promise<void> {
    if (!isNativePlatform()) return;
    try {
      const mod = (await import(/* @vite-ignore */ '@capacitor/app')) as {
        App?: { exitApp?: () => Promise<void> };
      };
      if (mod.App && typeof (mod.App as { openSettings?: unknown }).openSettings === 'function') {
        await (mod.App as unknown as { openSettings: () => Promise<void> }).openSettings();
      }
    } catch (err) {
      console.warn('[MobilePushService] openSystemSettings failed:', err);
    }
  }

  /**
   * Test entry-point + production hook used by the lazy-imported plugin
   * listeners. Exposed so the spec can simulate a tap without the
   * Capacitor runtime.
   */
  /**
   * F-SDK REQ-F-SDK-040 — accepts a push token the host application obtained
   * from APNs or FCM and registers it against the calling device session.
   *
   * The backend contract needs no change: `POST /api/mobile/device/push-token`
   * already takes `{ token, platform }` and derives the provider server-side,
   * keyed on `(DeviceInstallId, TenantId)`.
   */
  async registerHostPushToken(token: string, platform: 'ios' | 'android'): Promise<void> {
    if (!token) return;
    this._registeredToken.set(token);
    await this.registerTokenWithBff(token, platform);
  }

  handleTap(tap: MobilePushTap): PushRoutingDecision {
    const decision = this.decodeTapTarget(tap);
    switch (decision.kind) {
      case 'deep-link-url':
        if (decision.url) this.deepLink.handleUrl(decision.url);
        break;
      case 'target-surface':
        if (decision.target) this.deepLink.handleTarget(decision.target);
        break;
      case 'noop':
        // Nothing routable in the payload — leave the app on its current
        // page (cold-launch already lands on `/your-events`).
        break;
    }
    return decision;
  }

  /**
   * Pure decoder used by `handleTap()` and unit tests. Never touches the
   * Router — caller drives the routing decision.
   */
  decodeTapTarget(tap: MobilePushTap | null | undefined): PushRoutingDecision {
    if (!tap) return { kind: 'noop' };
    const data: MobilePushPayloadData = tap.data ?? {};

    // Structured target wins over a free-form deep-link URL — the BRD
    // describes the structured shape as the default emitted by the BFF.
    const landingType = typeof data.landingType === 'string' ? data.landingType : null;
    if (landingType) {
      const target: TargetSurface = {
        landingType,
        landingTargetId: parseTargetId(data.landingTargetId) ?? null
      };
      return { kind: 'target-surface', target };
    }
    if (typeof data.deepLink === 'string' && data.deepLink.length > 0) {
      return { kind: 'deep-link-url', url: data.deepLink };
    }
    return { kind: 'noop' };
  }

  /** Internal — register native push listeners. Idempotent. */
  private async installListeners(): Promise<void> {
    if (this.listenerInstalled) return;
    if (!isNativePlatform() || !isPluginAvailable('PushNotifications')) return;
    this.listenerInstalled = true;
    try {
      const mod = (await import(/* @vite-ignore */ '@capacitor/push-notifications')) as {
        PushNotifications?: {
          addListener: (
            event:
              | 'registration'
              | 'registrationError'
              | 'pushNotificationReceived'
              | 'pushNotificationActionPerformed',
            listener: (data: unknown) => void
          ) => unknown;
        };
      };
      if (!mod.PushNotifications) return;
      mod.PushNotifications.addListener('registration', (data: unknown) => {
        const t = (data as { value?: string } | null)?.value;
        if (typeof t === 'string' && t.length > 0) {
          void this.onRegistration(t);
        }
      });
      mod.PushNotifications.addListener('registrationError', (err) => {
        console.warn('[MobilePushService] registrationError:', err);
      });
      mod.PushNotifications.addListener('pushNotificationActionPerformed', (action: unknown) => {
        const a = (action as { notification?: { data?: MobilePushPayloadData; id?: string } } | null)
          ?.notification;
        this.handleTap({ notificationId: a?.id, data: a?.data ?? {} });
      });
    } catch (err) {
      console.warn('[MobilePushService] Failed to bind push listeners:', err);
      this.listenerInstalled = false;
    }
  }

  /** Internal — called from the `registration` listener. */
  private async onRegistration(token: string): Promise<void> {
    this._registeredToken.set(token);
    await this.registerTokenWithBff(token);
  }

  /**
   * POST the device's APNs / FCM token to the Stage 9 endpoint. The BFF
   * persists / refreshes the row in `mobile.PushToken` and revokes any
   * stale row for the same `(DeviceInstallId, TenantId)`.
   */
  async registerTokenWithBff(token: string, platformOverride?: 'ios' | 'android'): Promise<void> {
    const access = this.auth.getToken();
    const deviceSessionId = this.auth.getActiveDeviceSessionId();
    if (!access || deviceSessionId == null) {
      // Not yet authenticated — store the token in memory so the next
      // sign-in completion can push it up. AuthService.persistLogin
      // calls `flushPendingToken()` post-login.
      return;
    }
    const descriptor = await this.device.describe();
    try {
      await firstValueFrom(
        this.http.post(
          `${environment.apiUrl}/api/mobile/device/push-token`,
          // F-SDK: the host reports which push service issued the token. It
          // is authoritative over our own platform probe, because the
          // embedded web layer only knows the OS, not which of the host's
          // providers the token came from.
          { token, platform: platformOverride ?? descriptor.platform },
          {
            headers: new HttpHeaders({
              'Content-Type': 'application/json',
              Authorization: `Bearer ${access}`,
              'X-Mobile-Device-Session': String(deviceSessionId)
            })
          }
        )
      );
    } catch (err) {
      if (err instanceof HttpErrorResponse) {
        console.warn(
          '[MobilePushService] push-token register failed:',
          err.status,
          err.error
        );
      } else {
        console.warn('[MobilePushService] push-token register failed:', err);
      }
    }
  }
}

function parseTargetId(value: MobilePushPayloadData['landingTargetId']): number | null {
  if (value == null) return null;
  const n = typeof value === 'string' ? Number(value) : value;
  return Number.isFinite(n) && n > 0 ? n : null;
}
